#!/usr/bin/env node
/* oxlint-disable import/max-dependencies -- CLI entry point wires every runner variant (task/pm/code-review/adhoc) + setup; splitting would scatter the boot sequence */
import { readFileSync } from "node:fs";
import { join, dirname } from "node:path";
import { fileURLToPath } from "node:url";
import { SessionRunner } from "./runner/session-runner.js";
import type { AgentRunnerStatus, RunnerMode } from "@project/shared";
import { createServiceLogger } from "./utils/logger.js";
import {
  applyBootstrapToEnv,
  awaitGitReady,
  buildSessionPreviewPorts,
  fetchBootstrap,
  loadConveyorConfig,
  loadForwardPorts,
  runSetupCommand,
  runStartCommand,
  startLazySidecars,
  waitForSidecars,
} from "./setup/index.js";
import { checkSessionTaskIdentity } from "./utils/session-identity.js";
import {
  buildAgentExitLog,
  exitLogLevel,
  type AgentExitIdentity,
  type AgentExitReason,
} from "./utils/agent-exit.js";
import { resolveProjectRunnerIdentity } from "./setup/project-identity.js";
import { ProjectSessionRunner } from "./runner/project-session-runner.js";
import { AdhocSessionRunner } from "./runner/adhoc-session-runner.js";
import { ReviewChildSupervisor } from "./runner/review-child.js";
import { SessionChildSupervisor } from "./runner/session-child.js";
import { createSpawnedChildRunner } from "./runner/spawned-child-boot.js";

// Handle --version flag before any other initialization
if (process.argv.includes("--version")) {
  const __dirname = dirname(fileURLToPath(import.meta.url));
  const pkgPath = join(__dirname, "..", "package.json");
  const pkg = JSON.parse(readFileSync(pkgPath, "utf-8"));
  process.stdout.write(pkg.version + "\n");
  process.exit(0);
}

const logger = createServiceLogger("CLI");

// Pod-exit observability. The reconciler can only infer *that* an agent stopped
// (stale heartbeat → Stranded); it never sees *why*. We log the cause from the
// pod side at every catchable exit, keyed on the same sessionId the API's
// `session_stranded` line carries so the two ends can be joined. Populated once
// env identity resolves below; the early crash handlers read it live. (SIGKILL /
// OOM 137 is uncatchable — diagnosed from GKE kernel logs, not this line.)
const exitContext: AgentExitIdentity = {};
let exitLogged = false;
function logAgentExit(
  reason: AgentExitReason,
  opts: { exitCode: number; signal?: string; finalState?: string },
): void {
  // One line per process death; force_timeout is logged separately (it escalates
  // an already-logged signal shutdown) so it bypasses this guard at its call site.
  if (exitLogged) return;
  exitLogged = true;
  const payload = buildAgentExitLog({
    reason,
    exitCode: opts.exitCode,
    signal: opts.signal,
    finalState: opts.finalState,
    uptimeSec: process.uptime(),
    identity: exitContext,
  });
  logger[exitLogLevel(reason)]("agent_exit", payload as unknown as Record<string, unknown>);
}

async function bootstrapFromCodespace(apiUrl: string, instanceName: string): Promise<void> {
  const bootstrapToken = process.env.CONVEYOR_BOOTSTRAP_TOKEN;
  const apiUrlFromEnv = Boolean(process.env.CONVEYOR_API_URL);
  logger.info("Bootstrapping from codespace", {
    codespace: instanceName,
    apiUrl,
    apiUrlFromEnv,
    bootstrapTokenPresent: Boolean(bootstrapToken),
  });
  const result = await fetchBootstrap({
    apiUrl,
    instanceName,
    bootstrapToken,
  });
  if (!result.ok) {
    logger.error("Bootstrap failed after retries", {
      reason: result.reason,
      attempts: result.attempts,
      status: result.status,
      detail: result.detail,
      apiUrl,
      apiUrlFromEnv,
      bootstrapTokenPresent: Boolean(bootstrapToken),
      hint: apiUrlFromEnv
        ? "Verify the codespace was created by this Conveyor deployment and that the bootstrap token is current."
        : "CONVEYOR_API_URL was not set as a codespace secret — agent fell back to the built-in default. Re-create the codespace, or push CONVEYOR_API_URL via project settings.",
    });
    process.exit(1);
  }
  applyBootstrapToEnv(result.config);
  logger.info("Bootstrap complete", {
    taskId: result.config.taskId,
    attempts: result.attempts,
  });
}

// Helper to detect expected abort errors from SDK cleanup
function isExpectedAbortError(error: Error | NodeJS.ErrnoException): boolean {
  const message = error.message || "";
  const hasAbortMessage = /operation aborted/i.test(message) || /abort/i.test(message);
  const hasAbortCode = !("code" in error) || error.code === undefined || error.code === "ABORT_ERR";
  return hasAbortMessage && hasAbortCode;
}

process.on("uncaughtException", (err: NodeJS.ErrnoException) => {
  if (err.code === "EPIPE") return;

  if (isExpectedAbortError(err)) {
    logger.info("Ignored expected abort after shutdown", { error: err.message, code: err.code });
    return;
  }

  logger.error("Uncaught exception", { error: err.message, code: err.code });
  logAgentExit("uncaught_exception", { exitCode: 1 });
  process.exit(1);
});

process.on("unhandledRejection", (reason: unknown) => {
  const err = reason instanceof Error ? reason : new Error(String(reason));

  if (isExpectedAbortError(err)) {
    logger.info("Ignored expected abort rejection after shutdown", { error: err.message });
    return;
  }

  logger.error("Unhandled rejection", { error: err.message });
  logAgentExit("unhandled_rejection", { exitCode: 1 });
  process.exit(1);
});

const DEFAULT_CONVEYOR_API_URL = "https://api.conveyor.rallycryapp.com";
let conveyorApiUrl = process.env.CONVEYOR_API_URL || DEFAULT_CONVEYOR_API_URL;

// Step 1: Codespace bootstrap
const INSTANCE_NAME = process.env.CODESPACE_NAME || process.env.CLAUDESPACE_NAME;
if (INSTANCE_NAME && !process.env.CONVEYOR_TASK_TOKEN) {
  if (!conveyorApiUrl) {
    logger.error("Could not resolve CONVEYOR_API_URL for codespace bootstrap");
    process.exit(1);
  }
  await bootstrapFromCodespace(conveyorApiUrl, INSTANCE_NAME);
  conveyorApiUrl = process.env.CONVEYOR_API_URL ?? conveyorApiUrl;
}

// Step 2: Read env vars (bootstrap may have set them)
const CONVEYOR_TASK_TOKEN = process.env.CONVEYOR_TASK_TOKEN;
const CONVEYOR_TASK_ID = process.env.CONVEYOR_TASK_ID;
const CONVEYOR_WORKSPACE = process.env.CONVEYOR_WORKSPACE ?? process.cwd();
const CONVEYOR_MODE = (process.env.CONVEYOR_MODE ?? "task") as RunnerMode;
const CONVEYOR_AGENT_MODE = process.env.CONVEYOR_AGENT_MODE || undefined;
const CONVEYOR_IS_AUTO = CONVEYOR_AGENT_MODE
  ? CONVEYOR_AGENT_MODE === "auto"
  : process.env.CONVEYOR_IS_AUTO === "true";

// Seed the pod-exit identity now that env is resolved (the specific runner
// branches below refine sessionId/workspaceId to their own session).
exitContext.taskId = CONVEYOR_TASK_ID;
exitContext.workspaceId = process.env.CONVEYOR_WORKSPACE_ID;
exitContext.runnerMode = CONVEYOR_MODE;
exitContext.sessionId = process.env.CONVEYOR_SESSION_ID ?? CONVEYOR_TASK_ID;

// Step 2.5: task-less PROJECT pods (Claudespace v3 project runners). The
// bundle's session JWT carries {projectId, sessionId} and NO taskId; the
// entrypoint exports CONVEYOR_PROJECT_ID (no CONVEYOR_TASK_ID) and sets
// CONVEYOR_MODE=pm. Boot the project runner: connect + register session +
// heartbeat + idle awaiting project-scoped messages. Never falls through to
// the task lifecycle below.
const projectIdentity = resolveProjectRunnerIdentity(process.env);

// Task-less ADHOC pods (Sessions view scratch pods). Same task-less identity as
// a project pod (projectId + sessionId, no taskId), distinguished by
// CONVEYOR_MODE=adhoc. Runs an interactive `claude` TUI relayed to the web
// terminal — NOT the autonomous pm project runner below. Checked first so an
// adhoc session never falls through to pm.
if (!CONVEYOR_TASK_ID && projectIdentity && CONVEYOR_MODE === "adhoc") {
  logger.info("Starting ad-hoc agent", { projectId: projectIdentity.projectId });
  const adhocRunner = new AdhocSessionRunner(
    {
      connection: {
        apiUrl: conveyorApiUrl ?? "",
        taskToken: projectIdentity.taskToken,
        sessionId: projectIdentity.sessionId,
        runnerMode: "adhoc",
      },
      projectId: projectIdentity.projectId,
      workspaceId: projectIdentity.workspaceId,
      workspaceDir: CONVEYOR_WORKSPACE,
      ...(process.env.CLAUDESPACE_NAME ? { lifecycle: { idleTimeoutMs: 60 * 60 * 1000 } } : {}),
    },
    {
      onEvent: (event) => {
        logger.info("Ad-hoc runner event", { eventType: event.type as string });
      },
    },
  );
  exitContext.sessionId = projectIdentity.sessionId;
  exitContext.workspaceId = projectIdentity.workspaceId;
  exitContext.runnerMode = "adhoc";
  process.on("SIGTERM", () => adhocRunner.stop());
  process.on("SIGINT", () => adhocRunner.stop());
  await adhocRunner.run();
  const adhocError = adhocRunner.finalState === "error";
  logAgentExit(adhocError ? "error" : "clean", {
    exitCode: adhocError ? 1 : 0,
    finalState: adhocRunner.finalState ?? undefined,
  });
  process.exit(adhocError ? 1 : 0);
}

if (!CONVEYOR_TASK_ID && projectIdentity) {
  logger.info("Starting project agent", { projectId: projectIdentity.projectId });
  const projectRunner = new ProjectSessionRunner(
    {
      connection: {
        apiUrl: conveyorApiUrl ?? "",
        taskToken: projectIdentity.taskToken,
        sessionId: projectIdentity.sessionId,
        runnerMode: "pm",
      },
      projectId: projectIdentity.projectId,
      // Same extended idle window claudespace task pods get.
      ...(process.env.CLAUDESPACE_NAME ? { lifecycle: { idleTimeoutMs: 60 * 60 * 1000 } } : {}),
    },
    {
      onEvent: (event) => {
        logger.info("Project runner event", { eventType: event.type as string });
      },
    },
  );
  exitContext.sessionId = projectIdentity.sessionId;
  exitContext.workspaceId = projectIdentity.workspaceId;
  exitContext.runnerMode = "pm";
  process.on("SIGTERM", () => projectRunner.stop());
  process.on("SIGINT", () => projectRunner.stop());
  await projectRunner.run();
  const projectError = projectRunner.finalState === "error";
  logAgentExit(projectError ? "error" : "clean", {
    exitCode: projectError ? 1 : 0,
    finalState: projectRunner.finalState ?? undefined,
  });
  process.exit(projectError ? 1 : 0);
}

if (!CONVEYOR_TASK_TOKEN || !CONVEYOR_TASK_ID) {
  logger.error("Missing required environment variables");
  logger.error("  CONVEYOR_TASK_TOKEN - JWT token for task authentication");
  logger.error("  CONVEYOR_TASK_ID - ID of the task to execute");
  logger.error("");
  logger.error("CONVEYOR_API_URL is provided via codespace secret or bootstrap.");
  logger.error("");
  logger.error("Optional:");
  logger.error("  CONVEYOR_MODE - Runner mode: 'task' (default), 'pack', or 'pm'");
  logger.error("  CONVEYOR_WORKSPACE - Working directory (defaults to cwd)");
  logger.error(
    "  Project pods instead require CONVEYOR_PROJECT_ID + CONVEYOR_SESSION_ID + CONVEYOR_MODE=pm",
  );
  process.exit(1);
}

if (
  CONVEYOR_MODE !== "task" &&
  CONVEYOR_MODE !== "pm" &&
  CONVEYOR_MODE !== "code-review" &&
  CONVEYOR_MODE !== "adhoc" &&
  CONVEYOR_MODE !== "pack" &&
  CONVEYOR_MODE !== "shell"
) {
  logger.error("Invalid CONVEYOR_MODE", {
    mode: CONVEYOR_MODE,
    expected: ["task", "pm", "code-review", "adhoc", "pack", "shell"],
  });
  process.exit(1);
}

// Same-pod spawned children (session:spawnTui): TASK-BOUND extra tabs on the
// builder's pod — a second Claude TUI (mode "adhoc" + a task id, unlike the
// task-less Sessions-view branch above) or a raw login shell (mode "shell").
// Both bind their own WorkspaceSession via env overrides set by
// SessionChildSupervisor and never fall through to the task SessionRunner.
if (CONVEYOR_MODE === "shell" || CONVEYOR_MODE === "adhoc") {
  const spawnedSessionId = process.env.CONVEYOR_SESSION_ID;
  if (!spawnedSessionId) {
    logger.error("Spawned child requires CONVEYOR_SESSION_ID", { mode: CONVEYOR_MODE });
    process.exit(1);
  }
  exitContext.sessionId = spawnedSessionId;
  exitContext.runnerMode = CONVEYOR_MODE;

  const childRunner = createSpawnedChildRunner({
    mode: CONVEYOR_MODE,
    apiUrl: conveyorApiUrl ?? "",
    taskToken: CONVEYOR_TASK_TOKEN,
    sessionId: spawnedSessionId,
    workspaceDir: CONVEYOR_WORKSPACE,
    projectId: process.env.CONVEYOR_PROJECT_ID ?? "",
  });
  logger.info("Starting spawned session child", {
    mode: CONVEYOR_MODE,
    sessionId: spawnedSessionId,
  });
  process.on("SIGTERM", () => childRunner.stop());
  process.on("SIGINT", () => childRunner.stop());
  await childRunner.run();
  const childError = childRunner.finalState === "error";
  logAgentExit(childError ? "error" : "clean", {
    exitCode: childError ? 1 : 0,
    finalState: childRunner.finalState ?? undefined,
  });
  process.exit(childError ? 1 : 0);
}

logger.info("Starting agent", { mode: CONVEYOR_MODE });

// Claudespace pods: longer idle timeout to stay alive for incoming messages.
// Non-claudespace environments (local, workspace, GitHub Codespaces) have
// persistent state and don't need the periodic WIP commit safety net, so
// disable the git flush timer for them.
const lifecycleOverrides = process.env.CLAUDESPACE_NAME
  ? { idleTimeoutMs: 60 * 60 * 1000 }
  : { gitFlushIntervalMs: 0 };

const runner = new SessionRunner(
  {
    connection: {
      apiUrl: conveyorApiUrl ?? "",
      taskToken: CONVEYOR_TASK_TOKEN,
      // CONVEYOR_SESSION_ID is the CodespaceSession ID for BaseService ACL.
      // Falls back to CONVEYOR_TASK_ID for backward compat (codespace bootstrap sets only task ID).
      sessionId: process.env.CONVEYOR_SESSION_ID ?? CONVEYOR_TASK_ID,
      runnerMode: CONVEYOR_MODE,
    },
    runnerMode: CONVEYOR_MODE,
    isAuto: CONVEYOR_IS_AUTO,
    workspaceDir: CONVEYOR_WORKSPACE,
    agentMode: CONVEYOR_AGENT_MODE as import("@project/shared").AgentMode | undefined,
    lifecycle: lifecycleOverrides,
  },
  {
    onStatusChange: (status: AgentRunnerStatus) => {
      logger.info("Status changed", { status });
    },
    onEvent: (event: Record<string, unknown>) => {
      const detail =
        (event.message as string) ?? (event.content as string) ?? (event.summary as string) ?? "";
      if (detail) {
        logger.info(detail, { eventType: event.type as string });
      }
    },
  },
);

// Same-pod review children (session:spawnReview). Wired only for the builder
// ("task") runner mode below — a review child must never recursively spawn.
const reviewChildren = new ReviewChildSupervisor(runner.connection, CONVEYOR_WORKSPACE);

// Same-pod TUI/shell children (session:spawnTui). Wired for builder-shaped
// runners (task + pack) below — spawned children run adhoc/shell modes, which
// exit above before this block, so recursion is impossible.
const sessionChildren = new SessionChildSupervisor(runner.connection, CONVEYOR_WORKSPACE);

let shutdownSignal: "SIGTERM" | "SIGINT" | undefined;

const shutdownAgent = (signal: "SIGTERM" | "SIGINT") => {
  logger.info(`Received ${signal}, flushing git and stopping agent`);
  // Recorded (not logged yet) so the final agent_exit line — emitted once the
  // real exit code is known below — can still attribute the cause to this
  // signal. A graceful SIGTERM resolves runner.run() with finalState="finished"
  // and exits 0, so guessing exitCode here up front would misreport it as 1.
  shutdownSignal = signal;
  // Flush WIP commit + push BEFORE stop() tears down the connection, since
  // the token-refresh RPC needs a live socket. Then stop the runner.
  void (async () => {
    try {
      await Promise.all([reviewChildren.stopAll(), sessionChildren.stopAll()]);
    } catch {
      /* best-effort */
    }
    try {
      await runner.flushGitOnShutdown();
    } finally {
      runner.stop();
    }
  })();
  setTimeout(() => {
    logger.warn(`Forcing exit after ${signal} timeout`);
    // The graceful flush/stop hung past the watchdog — emit directly (bypassing
    // the one-line guard in logAgentExit) since run() will never resolve to log
    // this exit itself.
    logger.warn(
      "agent_exit",
      buildAgentExitLog({
        reason: "force_timeout",
        exitCode: 1,
        signal,
        finalState: runner.finalState ?? undefined,
        uptimeSec: process.uptime(),
        identity: exitContext,
      }) as unknown as Record<string, unknown>,
    );
    process.exit(1);
  }, 45_000).unref();
};

process.on("SIGTERM", () => shutdownAgent("SIGTERM"));
process.on("SIGINT", () => shutdownAgent("SIGINT"));

// Connect first so setup/start output is forwarded to the API
await runner.connect();

const logSetupOutput = (stream: "stdout" | "stderr", data: string) => {
  runner.connection.sendEvent({ type: "setup_output", stream, data });
  (stream === "stderr" ? process.stderr : process.stdout).write(data);
};

type StartCommandChild = ReturnType<typeof runStartCommand>;

let startCommandChild: StartCommandChild | null = null;
const expectedStartCommandStops = new WeakSet<StartCommandChild>();

const stopStartCommandChild = (): void => {
  const child = startCommandChild;
  if (!child || child.exitCode !== null || child.killed) return;
  expectedStartCommandStops.add(child);
  try {
    if (child.pid) process.kill(-child.pid, "SIGTERM");
    else child.kill("SIGTERM");
  } catch {
    try {
      child.kill("SIGTERM");
    } catch {
      /* already exited */
    }
  }
};

const launchStartCommand = (command: string): void => {
  logger.info("Running start command", { command });
  runner.connection.sendEvent({ type: "start_command_started" });
  const child = runStartCommand(command, CONVEYOR_WORKSPACE, (stream, data) => {
    runner.connection.sendEvent({ type: "start_command_output", stream, data });
    (stream === "stderr" ? process.stderr : process.stdout).write(data);
  });
  startCommandChild = child;
  child.on("exit", (code, signal) => {
    if (startCommandChild === child) startCommandChild = null;
    if (expectedStartCommandStops.has(child)) return;

    logger.info("Start command exited", { code, signal });
    runner.connection.sendEvent({
      type: "start_command_exited",
      code,
      signal,
      message: `start command exited${code === null ? "" : ` with code ${code}`}${signal ? ` (signal: ${signal})` : ""}`,
    });
    // Surface non-zero exits as a loud chat message too, so users know
    // their dev server crashed and the agent may hit tool-use failures.
    if (code !== null && code !== 0) {
      runner.connection.sendEvent({
        type: "start_command_error",
        message: `start command exited with code ${code}${signal ? ` (signal: ${signal})` : ""}`,
      });
    }
  });
  child.on("error", (err) => {
    if (startCommandChild === child) startCommandChild = null;
    logger.error("Start command error", { error: err.message });
    runner.connection.sendEvent({ type: "start_command_error", message: err.message });
  });
};

// Wake lazy sidecars as soon as the agent is connected. Readiness still gates
// only setup/start below; Claude startup remains gated only on workspace git.
void startLazySidecars(process.env, (message) =>
  logSetupOutput("stdout", `[sidecars] ${message}\n`),
);

// Defense-in-depth: warn loudly if CONVEYOR_SESSION_ID decodes to a different
// task than CONVEYOR_TASK_ID. Authoritative checks are server-side
// (requireTaskAuth + partial-unique index on active CodespaceSessions). This
// is a visibility net in case those regress — see SEC-9 postmortem in
// .claude/rules/agent-codespace.md.
void checkSessionTaskIdentity({
  sessionId: process.env.CONVEYOR_SESSION_ID,
  taskId: CONVEYOR_TASK_ID,
  fetchSessionTaskId: async (sessionId) => {
    const ctx = await runner.connection.call("getTaskContext", { sessionId });
    return ctx.id;
  },
  logger,
});

// Run setup + start commands in the background so the agent can start
// fetching context and thinking immediately. The dev environment will be
// ready by the time the agent needs to actually execute anything in it.
// Start command still runs after setup (install must finish before `bun
// run dev` works), but both are decoupled from runner.run().
//
// INVARIANT: failures inside this IIFE are NON-FATAL. Any throw or non-zero
// exit from setup/start must surface as a chat event, never as process.exit.
// The agent continues so the user can still interact with it and debug.
// Same-pod review: only the builder runner hosts review children. The push
// arrives on the BUILDER's session room; the child binds the review session
// via env overrides (its own JWT/sessionId, CONVEYOR_MODE=code-review).
if (CONVEYOR_MODE === "task") {
  runner.connection.onSpawnReview((data) => {
    void reviewChildren.spawn(data).catch((err: unknown) => {
      logger.error("Review child spawn threw", {
        error: err instanceof Error ? err.message : String(err),
      });
      runner.connection.reportReviewSpawnFailure(
        data.sessionId,
        err instanceof Error ? err.message : String(err),
      );
    });
  });
}

// Same-pod TUI/shell tabs: builder-shaped runners (task + pack) host spawned
// children. The push arrives on the BUILDER's session room; each child binds
// its own session via env overrides (JWT/sessionId, CONVEYOR_MODE=adhoc|shell).
if (CONVEYOR_MODE === "task" || CONVEYOR_MODE === "pack") {
  runner.connection.onSpawnTui((data) => {
    void sessionChildren.spawn(data).catch((err: unknown) => {
      logger.error("Session child spawn threw", {
        error: err instanceof Error ? err.message : String(err),
      });
      runner.connection.reportSessionSpawnFailure(
        data.sessionId,
        err instanceof Error ? err.message : String(err),
      );
    });
  });
}

const conveyorConfig = loadConveyorConfig();
runner.connection.onRunStartCommand(() => {
  if (!conveyorConfig?.startCommand) return;
  runner.connection.sendEvent({
    type: "start_command_output",
    stream: "stdout",
    data: "[conveyor-agent] Restarting start command...\n",
  });
  stopStartCommandChild();
  launchStartCommand(conveyorConfig.startCommand);
});

if (conveyorConfig) {
  void (async () => {
    // Gate setup/start on the entrypoint's backgrounded git preparation: these
    // scripts need the repo present. If git never became ready, skip them —
    // non-fatal, the agent keeps running so the user can still interact/debug.
    // "not-gated" (GitHub Codespaces / local) and "ready" both proceed.
    const gitState = await awaitGitReady({
      onLog: (m) => logSetupOutput("stdout", `[git] ${m}\n`),
    });
    if (gitState === "failed" || gitState === "timeout") {
      runner.connection.sendEvent({
        type: "setup_error",
        message: "Workspace not ready — skipping setup/start",
      });
      return;
    }

    // Gate setup/start on sidecar readiness. This wait used to live in the pod
    // entrypoint AHEAD of the agent launch, blocking the thinking loop on
    // postgres/firebase boot it never needed. It now runs here — inside the
    // background IIFE, in parallel with runner.run() — so it only delays
    // setup/start (which genuinely need a warm postgres), not the agent.
    await waitForSidecars({
      onLog: (message) => logSetupOutput("stdout", `[sidecars] ${message}\n`),
      startLazy: false,
    });

    if (conveyorConfig.setupCommand) {
      logger.info("Running setup command (background)", {
        command: conveyorConfig.setupCommand,
      });
      try {
        await runSetupCommand(conveyorConfig.setupCommand, CONVEYOR_WORKSPACE, logSetupOutput);
        logger.info("Setup command completed");
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Setup command failed";
        logger.error("Setup command failed", { error: msg });
        runner.connection.sendEvent({ type: "setup_error", message: msg });
      }
    }

    let startCommandRunning = false;
    if (conveyorConfig.startCommand) {
      launchStartCommand(conveyorConfig.startCommand);
      startCommandRunning = true;
    }

    const forwardPorts = await loadForwardPorts(CONVEYOR_WORKSPACE);
    const previewPorts = buildSessionPreviewPorts(forwardPorts);
    runner.connection.sendEvent({
      type: "setup_complete",
      startCommandRunning,
      ...(previewPorts.length > 0 ? { previewPorts } : {}),
    });
  })();
}

// Start the main agent lifecycle immediately in parallel with setup
// (context fetch, mode resolution, core loop).
runner
  .run()
  .then(() => {
    // Exit with code 0 for clean shutdown (entrypoint won't restart),
    // code 1 for errors (entrypoint will restart). A SIGTERM-initiated shutdown
    // resolves run() too (finalState="finished"), so tag it "signal" rather
    // than "clean" — but the exit code still follows the real finalState, not
    // a guess made back when the signal first arrived.
    const errored = runner.finalState === "error";
    const reason: AgentExitReason = shutdownSignal ? "signal" : errored ? "error" : "clean";
    logAgentExit(reason, {
      exitCode: errored ? 1 : 0,
      signal: shutdownSignal,
      finalState: runner.finalState ?? undefined,
    });
    process.exit(errored ? 1 : 0);
  })
  .catch((error: unknown) => {
    const msg = error instanceof Error ? error.message : String(error);
    logger.error("Agent runner failed", { error: msg });
    logAgentExit("error", { exitCode: 1, finalState: runner.finalState ?? undefined });
    process.exit(1);
  });
